'use client'

import { useState, useRef } from 'react'
import Link from 'next/link'
import { File as FileIcon, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ThankYouMark } from '@/components/forms/ThankYouMark'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { StepIndicator } from '@/components/appraisal/StepIndicator'
import {
  AddressSummaryButton,
  StepActions,
  StepError,
  StepPrompt,
} from '@/components/appraisal/StepParts'
import {
  AddressAutocomplete,
  FieldLabel,
  ReviewSection,
  SignaturePad,
} from '@/components/offer/OfferForm'
import { omitContact, reportToLeadCentre } from '@/utilities/leadCentre'

/**
 * Commercial / Industrial Application Form — native rebuild of the old site's
 * JotForm (form.jotform.com/222910848027860), using the shared wizard chrome
 * (StepIndicator → address summary → prompt → controls → buttons) from the
 * appraisal and offer-to-purchase flows.
 *
 * 9 steps follow the JotForm's section order:
 *   1. Property            — address, building use, new/existing business
 *   2. Business            — legal name, trading name, ABN, years in business
 *   3. Applicants          — Director/Individual 1 (+ optional 2)
 *   4. Current tenancy     — business address, landlord/agent + contact consent
 *   5. Photo ID            — upload dropzone (required docs per the JotForm)
 *   6. Proposed lease terms
 *   7. References & other  — accountant + professional reference
 *   8. Declaration         — declaration text, acknowledgements, signature
 *   9. Review              — one block per section with its own "Change?"
 */

const TOTAL_STEPS = 9

interface FormState {
  propertyAddress: string
  buildingUse: string
  businessStatus: '' | 'new' | 'existing'
  yearsInBusiness: string
  legalName: string
  tradingName: string
  abn: string
  director1FirstName: string
  director1LastName: string
  director1Address: string
  email: string
  director1Phone: string
  director2FirstName: string
  director2LastName: string
  director2Address: string
  director2Email: string
  director2Phone: string
  currentBusinessAddress: string
  currentLandlord: string
  landlordEmail: string
  landlordPhone: string
  landlordContactOk: '' | 'yes' | 'no'
  landlordContactReason: string
  commencingRent: string
  commencementDate: string
  initialTerm: string
  leaseOptions: string
  rentIncreases: string
  bond: string
  tenancyUse: string
  specialConditions: string
  accountantName: string
  accountantCompany: string
  accountantEmail: string
  accountantPhone: string
  referenceName: string
  referenceCompany: string
  referenceEmail: string
  referencePhone: string
  referenceRelationship: string
  otherInfo: string
  declarationAgreed: boolean
  acknowledgeLeaseCosts: boolean
  acknowledgeWithdrawalCosts: boolean
  signedDate: string
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const EMAIL_RE = /^\S[^\s@]*@\S+\.\S+$/
const PHONE_RE = /^[\d\s+()-]{8,15}$/

/** A validation failure and the id of the control it belongs to ('' = whole step). */
interface StepProblem {
  field: string
  message: string
}

/**
 * Text field with the canonical uppercase label — the dominant control here.
 *
 * `error` puts the message directly under the field it belongs to and flips the
 * Input into its Figma error state. See `validateStep` for why that matters.
 */
function Field({
  id,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  inputMode,
  error,
}: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
  inputMode?: 'numeric' | 'tel' | 'email'
  error?: string
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        type={type}
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <div id={`${id}-error`}>
          <StepError>{error}</StepError>
        </div>
      )}
    </div>
  )
}

export function CommercialApplicationForm() {
  const [step, setStep] = useState(1)
  const [form, setForm] = useState<FormState>(() => ({
    propertyAddress: '',
    buildingUse: '',
    businessStatus: '',
    yearsInBusiness: '',
    legalName: '',
    tradingName: '',
    abn: '',
    director1FirstName: '',
    director1LastName: '',
    director1Address: '',
    email: '',
    director1Phone: '',
    director2FirstName: '',
    director2LastName: '',
    director2Address: '',
    director2Email: '',
    director2Phone: '',
    currentBusinessAddress: '',
    currentLandlord: '',
    landlordEmail: '',
    landlordPhone: '',
    landlordContactOk: '',
    landlordContactReason: '',
    commencingRent: '',
    commencementDate: '',
    initialTerm: '',
    leaseOptions: '',
    rentIncreases: '',
    bond: '',
    tenancyUse: '',
    specialConditions: '',
    accountantName: '',
    accountantCompany: '',
    accountantEmail: '',
    accountantPhone: '',
    referenceName: '',
    referenceCompany: '',
    referenceEmail: '',
    referencePhone: '',
    referenceRelationship: '',
    otherInfo: '',
    declarationAgreed: false,
    acknowledgeLeaseCosts: false,
    acknowledgeWithdrawalCosts: false,
    signedDate: todayISO(),
  }))
  const [files, setFiles] = useState<File[]>([])
  const [signature, setSignature] = useState<string | null>(null)
  const [error, setError] = useState<StepProblem | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const update = (patch: Partial<FormState>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    setError(null)
  }

  const goTo = (s: number) => {
    setError(null)
    setStep(s)
  }

  /**
   * Every problem carries the id of the control it belongs to (Asana 1217893549927821).
   *
   * The form previously returned a bare string and printed it once, at the bottom
   * of the step under the Next button. On step 3 — ten fields long — a missing
   * Director 1 email surfaced as "A valid email is required" sitting directly
   * beneath Director 2's Contact Number, so it read as that field demanding an
   * email, and the field actually at fault was scrolled off screen. Anchoring the
   * message to a control lets `next` render it in place and scroll to it.
   *
   * A blank `field` means the problem belongs to the step as a whole and falls
   * back to the message under the buttons.
   */
  const validateStep = (s: number): StepProblem | null => {
    if (s === 1) {
      if (!form.propertyAddress.trim())
        return { field: 'ca-address', message: 'Please enter the property address.' }
      if (!form.buildingUse.trim())
        return { field: 'ca-building-use', message: 'Please tell us the intended building use.' }
      if (!form.businessStatus)
        return {
          field: 'ca-business-status',
          message: 'Please tell us whether this is a new or existing business.',
        }
    }
    if (s === 3) {
      if (!form.director1FirstName.trim())
        return {
          field: 'ca-d1-first',
          message: 'Please enter Director / Individual 1’s first name.',
        }
      if (!form.director1LastName.trim())
        return { field: 'ca-d1-last', message: 'Please enter Director / Individual 1’s last name.' }
      if (!EMAIL_RE.test(form.email.trim()))
        return {
          field: 'ca-d1-email',
          message: 'Please enter a valid email address for Director / Individual 1.',
        }
      if (!PHONE_RE.test(form.director1Phone.trim()))
        return {
          field: 'ca-d1-phone',
          message: 'Please enter a valid contact number for Director / Individual 1.',
        }
    }
    if (s === 4) {
      if (!form.currentBusinessAddress.trim())
        return {
          field: 'ca-current-address',
          message: 'Please enter your current business address.',
        }
      if (!form.currentLandlord.trim())
        return {
          field: 'ca-landlord',
          message: 'Please enter your current landlord or managing agent.',
        }
      if (!form.landlordContactOk)
        return {
          field: 'ca-landlord-contact',
          message: 'Please tell us whether we can contact them.',
        }
    }
    if (s === 6) {
      if (!form.commencingRent.trim())
        return { field: 'ca-rent', message: 'Please enter the commencing rent.' }
      if (!form.commencementDate)
        return { field: 'ca-commencement', message: 'Please choose a commencement date.' }
      if (!form.initialTerm.trim())
        return { field: 'ca-term', message: 'Please enter the initial term.' }
      if (!form.leaseOptions.trim())
        return { field: 'ca-options', message: 'Please enter the options.' }
      if (!form.rentIncreases.trim())
        return { field: 'ca-increases', message: 'Please enter the annual rent increases.' }
      if (!form.bond.trim()) return { field: 'ca-bond', message: 'Please enter the bond.' }
      if (!form.tenancyUse.trim())
        return { field: 'ca-tenancy-use', message: 'Please enter the tenancy use.' }
    }
    if (s === 8) {
      if (!form.declarationAgreed)
        return { field: 'ca-declaration', message: 'Please confirm the declaration to continue.' }
      if (!form.acknowledgeLeaseCosts || !form.acknowledgeWithdrawalCosts)
        return {
          field: 'ca-acknowledgements',
          message: 'Please confirm both acknowledgements to continue.',
        }
      if (!signature) return { field: 'ca-signature', message: 'Please sign to continue.' }
    }
    return null
  }

  /** The message for `id`, or undefined — how each control asks whether it is at fault. */
  const errorFor = (id: string) => (error?.field === id ? error.message : undefined)

  /**
   * Bring the offending control into view and, where it takes a caret, focus it.
   * Deferred a frame so the error markup has rendered and the layout has settled
   * before we measure.
   */
  const revealField = (id: string) => {
    if (!id || typeof document === 'undefined') return
    requestAnimationFrame(() => {
      const el = document.getElementById(id)
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)
        el.focus({ preventScroll: true })
    })
  }

  const next = () => {
    const problem = validateStep(step)
    if (problem) {
      setError(problem)
      revealField(problem.field)
      return
    }
    setError(null)
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }

  const back = () => {
    setError(null)
    setStep((s) => Math.max(s - 1, 1))
  }

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list)
    setFiles((prev) => [...prev, ...incoming].slice(0, 10))
  }

  const submit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('payload', JSON.stringify({ ...form, signature: signature || undefined }))
      for (const file of files) body.append('documents', file)
      const res = await fetch('/api/commercial-applications', { method: 'POST', body })
      if (!res.ok) throw new Error('submit failed')
      setDone(true)

      // Commercial lease application — closest fit in the fixed lead_type enum is
      // RENTAL_ENQUIRY (the residential-Apply analogue); flagged as a judgment call
      // since it's commercial rather than residential (Lead Centre wiring brief).
      reportToLeadCentre({
        name: [form.director1FirstName, form.director1LastName].filter(Boolean).join(' '),
        email: form.email,
        phone: form.director1Phone,
        leadType: 'RENTAL_ENQUIRY',
        sourceForm: 'commercial-application',
        fields: omitContact(form, [
          'director1FirstName', 'director1LastName', 'email', 'director1Phone',
        ]),
      })
    } catch {
      setError({
        field: '',
        message: 'Something went wrong submitting your application. Please try again.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="flex flex-col items-start gap-6">
        <StepIndicator currentStep={TOTAL_STEPS} totalSteps={TOTAL_STEPS} />
        {/* Asana 018 — shared thank-you mark on all form success states */}
        <ThankYouMark />
        <h2 className="font-display text-display-2xl font-normal text-foreground">
          Thank you — your application has been received.
        </h2>
        <p className="max-w-xl text-body-md font-light text-foreground">
          A member of the Wilson Property commercial team will be in touch shortly about your
          application for <span className="font-normal">{form.propertyAddress}</span>. Approval from
          the Lessor may take several days.
        </p>
        <Button asChild>
          <Link href="/commercial-lease">Browse Commercial Properties</Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-8">
      <StepIndicator currentStep={step} totalSteps={TOTAL_STEPS} />

      {step > 1 && step < TOTAL_STEPS && (
        <AddressSummaryButton address={form.propertyAddress} onChange={() => goTo(1)} />
      )}

      {/* STEP 1 — Property */}
      {step === 1 && (
        <div className="w-full">
          <StepPrompt>Which property are you applying for?</StepPrompt>
          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel htmlFor="ca-address">Property Address *</FieldLabel>
              <AddressAutocomplete
                id="ca-address"
                value={form.propertyAddress}
                placeholder="Search for the address"
                onValueChange={(v) => update({ propertyAddress: v })}
              />
              {errorFor('ca-address') && <StepError>{errorFor('ca-address')}</StepError>}
            </div>
            <Field
              id="ca-building-use"
              label="Building Use *"
              value={form.buildingUse}
              onChange={(v) => update({ buildingUse: v })}
              placeholder="e.g. Retail, Office, Warehouse"
              error={errorFor('ca-building-use')}
            />
            <div id="ca-business-status">
              <p className="mb-6 text-body-md font-normal text-foreground">
                Is this a new or existing business? *
              </p>
              <RadioGroup
                name="ca-business-status"
                value={form.businessStatus}
                onValueChange={(v) => update({ businessStatus: v as 'new' | 'existing' })}
                className="flex flex-col gap-3"
              >
                {(
                  [
                    ['new', 'New Business'],
                    ['existing', 'Existing Business'],
                  ] as const
                ).map(([v, label]) => (
                  <label key={v} className="flex cursor-pointer items-center gap-2.5">
                    <RadioGroupItem value={v} />
                    <span className="text-body-sm font-normal text-foreground">{label}</span>
                  </label>
                ))}
              </RadioGroup>
              {errorFor('ca-business-status') && (
                <StepError>{errorFor('ca-business-status')}</StepError>
              )}
            </div>
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Start</Button>
          </StepActions>
        </div>
      )}

      {/* STEP 2 — Business */}
      {step === 2 && (
        <div className="w-full">
          <StepPrompt>Tell us about your business.</StepPrompt>
          <div className="flex flex-col gap-6">
            <Field
              id="ca-legal-name"
              label="Applicant (Legal Name)"
              value={form.legalName}
              onChange={(v) => update({ legalName: v })}
              placeholder="e.g. Smith Holdings Pty Ltd"
            />
            <Field
              id="ca-trading-name"
              label="Applicant (Trading Name)"
              value={form.tradingName}
              onChange={(v) => update({ tradingName: v })}
              placeholder="e.g. Smith's Cafe"
            />
            <Field
              id="ca-abn"
              label="ABN"
              value={form.abn}
              onChange={(v) => update({ abn: v })}
              inputMode="numeric"
              placeholder="XX XXX XXX XXX"
            />
            <Field
              id="ca-years"
              label="Years in Business"
              value={form.yearsInBusiness}
              onChange={(v) => update({ yearsInBusiness: v })}
              placeholder="e.g. 5"
            />
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 3 — Applicants */}
      {step === 3 && (
        <div className="w-full">
          <StepPrompt>Director / Individual details.</StepPrompt>
          <div className="flex flex-col gap-6">
            <p className="text-body-md font-normal text-foreground">Director / Individual 1</p>
            <Field
              id="ca-d1-first"
              label="First Name *"
              value={form.director1FirstName}
              onChange={(v) => update({ director1FirstName: v })}
              placeholder="e.g. Alex"
              error={errorFor('ca-d1-first')}
            />
            <Field
              id="ca-d1-last"
              label="Last Name *"
              value={form.director1LastName}
              onChange={(v) => update({ director1LastName: v })}
              placeholder="e.g. Smith"
              error={errorFor('ca-d1-last')}
            />
            <div>
              <FieldLabel htmlFor="ca-d1-address">Personal Address</FieldLabel>
              <AddressAutocomplete
                id="ca-d1-address"
                national
                value={form.director1Address}
                placeholder="Type your address"
                onValueChange={(v) => update({ director1Address: v })}
              />
            </div>
            <Field
              id="ca-d1-email"
              label="Email *"
              type="email"
              inputMode="email"
              value={form.email}
              onChange={(v) => update({ email: v })}
              placeholder="your@email.com"
              error={errorFor('ca-d1-email')}
            />
            <Field
              id="ca-d1-phone"
              label="Contact Number *"
              type="tel"
              inputMode="tel"
              value={form.director1Phone}
              onChange={(v) => update({ director1Phone: v })}
              placeholder="04XX XXX XXX"
              error={errorFor('ca-d1-phone')}
            />
            <p className="text-body-md font-normal text-foreground">
              Director / Individual 2 (if applicable)
            </p>
            <Field
              id="ca-d2-first"
              label="First Name"
              value={form.director2FirstName}
              onChange={(v) => update({ director2FirstName: v })}
            />
            <Field
              id="ca-d2-last"
              label="Last Name"
              value={form.director2LastName}
              onChange={(v) => update({ director2LastName: v })}
            />
            <div>
              <FieldLabel htmlFor="ca-d2-address">Personal Address</FieldLabel>
              <AddressAutocomplete
                id="ca-d2-address"
                national
                value={form.director2Address}
                placeholder="Type their address"
                onValueChange={(v) => update({ director2Address: v })}
              />
            </div>
            <Field
              id="ca-d2-email"
              label="Email"
              type="email"
              inputMode="email"
              value={form.director2Email}
              onChange={(v) => update({ director2Email: v })}
            />
            <Field
              id="ca-d2-phone"
              label="Contact Number"
              type="tel"
              inputMode="tel"
              value={form.director2Phone}
              onChange={(v) => update({ director2Phone: v })}
            />
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 4 — Current tenancy */}
      {step === 4 && (
        <div className="w-full">
          <StepPrompt>Your current tenancy.</StepPrompt>
          <div className="flex flex-col gap-6">
            <div>
              <FieldLabel htmlFor="ca-current-address">Current Business Address *</FieldLabel>
              <AddressAutocomplete
                id="ca-current-address"
                national
                value={form.currentBusinessAddress}
                placeholder="Type your business address"
                onValueChange={(v) => update({ currentBusinessAddress: v })}
              />
              {errorFor('ca-current-address') && (
                <StepError>{errorFor('ca-current-address')}</StepError>
              )}
            </div>
            <Field
              id="ca-landlord"
              label="Current Landlord / Managing Agent *"
              value={form.currentLandlord}
              onChange={(v) => update({ currentLandlord: v })}
              placeholder="Name and/or agency"
              error={errorFor('ca-landlord')}
            />
            <Field
              id="ca-landlord-email"
              label="Their Email"
              type="email"
              inputMode="email"
              value={form.landlordEmail}
              onChange={(v) => update({ landlordEmail: v })}
            />
            <Field
              id="ca-landlord-phone"
              label="Their Contact Number"
              type="tel"
              inputMode="tel"
              value={form.landlordPhone}
              onChange={(v) => update({ landlordPhone: v })}
            />
            <div id="ca-landlord-contact">
              <p className="mb-6 text-body-md font-normal text-foreground">
                Can we contact them? *
              </p>
              <RadioGroup
                name="ca-landlord-contact"
                value={form.landlordContactOk}
                onValueChange={(v) => update({ landlordContactOk: v as 'yes' | 'no' })}
                className="flex flex-col gap-3"
              >
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="flex cursor-pointer items-center gap-2.5">
                    <RadioGroupItem value={v} />
                    <span className="text-body-sm font-normal text-foreground">
                      {v === 'yes' ? 'Yes' : 'No'}
                    </span>
                  </label>
                ))}
              </RadioGroup>
              {errorFor('ca-landlord-contact') && (
                <StepError>{errorFor('ca-landlord-contact')}</StepError>
              )}
            </div>
            {form.landlordContactOk === 'no' && (
              <Field
                id="ca-landlord-why"
                label="If no, why not?"
                value={form.landlordContactReason}
                onChange={(v) => update({ landlordContactReason: v })}
              />
            )}
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 5 — Photo ID */}
      {step === 5 && (
        <div className="w-full">
          <StepPrompt>Please upload a copy of your photo ID.</StepPrompt>
          <div className="flex flex-col gap-2">
            <Label asChild>
              <span>
                Copies of photo ID are required to be submitted alongside your application.
              </span>
            </Label>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault()
                setDragOver(false)
                addFiles(e.dataTransfer.files)
              }}
              className={`flex h-16 w-full flex-col items-center justify-center gap-1.5 rounded-sm px-3 py-2 shadow-xs transition-colors ${
                dragOver ? 'bg-secondary' : 'bg-muted'
              }`}
            >
              <FileIcon className="size-6 text-muted-foreground" aria-hidden="true" />
              <span className="text-body-sm font-light text-muted-foreground">
                Select or drag and drop files.
              </span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,image/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            {files.length > 0 && (
              <ul className="mt-2 flex w-full flex-col gap-1.5">
                {files.map((file, i) => (
                  <li
                    key={`${file.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-sm border border-border px-3 py-1.5"
                  >
                    <span className="truncate text-body-sm font-light text-foreground">
                      {file.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      aria-label={`Remove ${file.name}`}
                      className="shrink-0 text-muted-foreground transition-opacity hover:opacity-70"
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-body-sm font-light text-foreground">
              Please{' '}
              <a href="mailto:commercial@wprci.com.au" className="underline">
                email us
              </a>{' '}
              to discuss if these are unavailable.
            </p>
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 6 — Proposed lease terms */}
      {step === 6 && (
        <div className="w-full">
          <StepPrompt>Proposed lease terms.</StepPrompt>
          <div className="flex flex-col gap-6">
            <Field
              id="ca-rent"
              label="Commencing Rent *"
              value={form.commencingRent}
              onChange={(v) => update({ commencingRent: v })}
              placeholder="$ per annum"
              error={errorFor('ca-rent')}
            />
            <div>
              <FieldLabel htmlFor="ca-commencement">Commencement Date *</FieldLabel>
              <Input
                id="ca-commencement"
                type="date"
                value={form.commencementDate}
                onChange={(e) => update({ commencementDate: e.target.value })}
                className="w-[248px]"
                aria-invalid={errorFor('ca-commencement') ? true : undefined}
              />
              {errorFor('ca-commencement') && <StepError>{errorFor('ca-commencement')}</StepError>}
            </div>
            <Field
              id="ca-term"
              label="Initial Term *"
              value={form.initialTerm}
              onChange={(v) => update({ initialTerm: v })}
              placeholder="e.g. 3 years"
              error={errorFor('ca-term')}
            />
            <Field
              id="ca-options"
              label="Options *"
              value={form.leaseOptions}
              onChange={(v) => update({ leaseOptions: v })}
              placeholder="e.g. 3 + 3 years"
              error={errorFor('ca-options')}
            />
            <Field
              id="ca-increases"
              label="Annual Rent Increases *"
              value={form.rentIncreases}
              onChange={(v) => update({ rentIncreases: v })}
              placeholder="e.g. 3% or CPI"
              error={errorFor('ca-increases')}
            />
            <Field
              id="ca-bond"
              label="Bond *"
              value={form.bond}
              onChange={(v) => update({ bond: v })}
              placeholder="e.g. 3 months' rent"
              error={errorFor('ca-bond')}
            />
            <Field
              id="ca-tenancy-use"
              label="Tenancy Use *"
              value={form.tenancyUse}
              onChange={(v) => update({ tenancyUse: v })}
              placeholder="e.g. Retail bakery"
              error={errorFor('ca-tenancy-use')}
            />
            <div>
              <FieldLabel htmlFor="ca-special">Special Conditions</FieldLabel>
              <Textarea
                id="ca-special"
                value={form.specialConditions}
                onChange={(e) => update({ specialConditions: e.target.value })}
                rows={3}
                placeholder="Type any special conditions here…"
              />
            </div>
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 7 — Professional references & other */}
      {step === 7 && (
        <div className="w-full">
          <StepPrompt>Professional references.</StepPrompt>
          <div className="flex flex-col gap-6">
            <p className="text-body-md font-normal text-foreground">Accountant</p>
            <Field
              id="ca-acc-name"
              label="Name"
              value={form.accountantName}
              onChange={(v) => update({ accountantName: v })}
            />
            <Field
              id="ca-acc-company"
              label="Company"
              value={form.accountantCompany}
              onChange={(v) => update({ accountantCompany: v })}
            />
            <Field
              id="ca-acc-email"
              label="Email"
              type="email"
              inputMode="email"
              value={form.accountantEmail}
              onChange={(v) => update({ accountantEmail: v })}
            />
            <Field
              id="ca-acc-phone"
              label="Phone"
              type="tel"
              inputMode="tel"
              value={form.accountantPhone}
              onChange={(v) => update({ accountantPhone: v })}
            />
            <p className="text-body-md font-normal text-foreground">Reference</p>
            <Field
              id="ca-ref-name"
              label="Name"
              value={form.referenceName}
              onChange={(v) => update({ referenceName: v })}
            />
            <Field
              id="ca-ref-company"
              label="Company"
              value={form.referenceCompany}
              onChange={(v) => update({ referenceCompany: v })}
            />
            <Field
              id="ca-ref-email"
              label="Email"
              type="email"
              inputMode="email"
              value={form.referenceEmail}
              onChange={(v) => update({ referenceEmail: v })}
            />
            <Field
              id="ca-ref-phone"
              label="Phone"
              type="tel"
              inputMode="tel"
              value={form.referencePhone}
              onChange={(v) => update({ referencePhone: v })}
            />
            <Field
              id="ca-ref-rel"
              label="Relationship"
              value={form.referenceRelationship}
              onChange={(v) => update({ referenceRelationship: v })}
              placeholder="e.g. Supplier, former landlord"
            />
            <div>
              <FieldLabel htmlFor="ca-other">
                Other: Is there anything you would like to tell us?
              </FieldLabel>
              <Textarea
                id="ca-other"
                value={form.otherInfo}
                onChange={(e) => update({ otherInfo: e.target.value })}
                rows={3}
                placeholder="Type your message here…"
              />
            </div>
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Next</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 8 — Declaration & signature */}
      {step === 8 && (
        <div className="w-full">
          <StepPrompt>Declaration.</StepPrompt>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 text-body-md font-light text-foreground">
              <p className="font-normal">
                By reading and signing this form you acknowledge the following:
              </p>
              <ul className="flex list-disc flex-col gap-2 pl-5">
                <li>
                  The rental applicant acknowledges that this is an application proposal to lease
                  this property and is subject to the Lessor&rsquo;s approval.
                </li>
                <li>
                  All terms listed are negotiable until formal documents are signed and executed by
                  all parties. Approval from the Lessor may take several days.
                </li>
                <li>
                  I hereby offer to rent the property from the Lessor under a Lease of Commercial
                  Real Estate to be prepared by the Lessor&rsquo;s relevant authority/solicitor.
                </li>
                <li>
                  Should the application be accepted by the Lessor, I agree to enter into a Lease of
                  Commercial Real Estate.
                </li>
              </ul>
            </div>
            <div id="ca-declaration">
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={form.declarationAgreed}
                  onCheckedChange={(checked) => update({ declarationAgreed: checked === true })}
                  className="mt-0.5"
                />
                <span className="text-body-sm font-normal text-foreground">
                  I agree — confirm declaration above. *
                </span>
              </label>
              {errorFor('ca-declaration') && <StepError>{errorFor('ca-declaration')}</StepError>}
            </div>
            <div id="ca-acknowledgements" className="flex flex-col gap-6">
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={form.acknowledgeLeaseCosts}
                  onCheckedChange={(checked) => update({ acknowledgeLeaseCosts: checked === true })}
                  className="mt-0.5"
                />
                <span className="text-body-sm font-normal text-foreground">
                  I/we acknowledge that upon acceptance of my offer to lease, the landlord will
                  engage a solicitor to prepare formal lease documentation, payable by the owner. *
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={form.acknowledgeWithdrawalCosts}
                  onCheckedChange={(checked) =>
                    update({ acknowledgeWithdrawalCosts: checked === true })
                  }
                  className="mt-0.5"
                />
                <span className="text-body-sm font-normal text-foreground">
                  I/we agree that if I choose to withdraw my application when a lease has been
                  prepared, I will be liable for the cost of legal document preparation. *
                </span>
              </label>
              {errorFor('ca-acknowledgements') && (
                <StepError>{errorFor('ca-acknowledgements')}</StepError>
              )}
            </div>
            <div id="ca-signature">
              <p className="mb-6 text-body-md font-normal text-foreground">Signature. *</p>
              <SignaturePad
                value={signature}
                onChange={(v) => {
                  setSignature(v)
                  setError(null)
                }}
              />
              {errorFor('ca-signature') && <StepError>{errorFor('ca-signature')}</StepError>}
            </div>
            <div>
              <p className="mb-6 text-body-md font-normal text-foreground">Today&rsquo;s date.</p>
              <Input
                type="date"
                aria-label="Today's date"
                value={form.signedDate}
                onChange={(e) => update({ signedDate: e.target.value })}
                className="w-[248px]"
              />
            </div>
            <p className="text-body-md font-light text-foreground">
              By submitting this form, you agree to our{' '}
              <Link
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={next}>Review</Button>
            <Button variant="ghost" onClick={back}>
              Back
            </Button>
          </StepActions>
        </div>
      )}

      {/* STEP 9 — Review */}
      {step === 9 && (
        <div className="flex w-full flex-col gap-8">
          <ReviewSection
            title="Property:"
            rows={[
              ['Address', form.propertyAddress],
              ['Building use', form.buildingUse],
              ['Business', form.businessStatus === 'new' ? 'New Business' : 'Existing Business'],
            ]}
            onChange={() => goTo(1)}
          />
          <ReviewSection
            title="Your business:"
            rows={[
              ...(form.legalName ? [['Legal name', form.legalName] as [string, string]] : []),
              ...(form.tradingName ? [['Trading name', form.tradingName] as [string, string]] : []),
              ...(form.abn ? [['ABN', form.abn] as [string, string]] : []),
              ...(form.yearsInBusiness
                ? [['Years in business', form.yearsInBusiness] as [string, string]]
                : []),
            ]}
            onChange={() => goTo(2)}
          />
          <ReviewSection
            title="Director / Individual 1:"
            rows={[
              ['Name', `${form.director1FirstName} ${form.director1LastName}`.trim()],
              ...(form.director1Address
                ? [['Personal address', form.director1Address] as [string, string]]
                : []),
              ['Email', form.email],
              ['Contact number', form.director1Phone],
            ]}
            onChange={() => goTo(3)}
          />
          {(form.director2FirstName || form.director2LastName) && (
            <ReviewSection
              title="Director / Individual 2:"
              rows={[
                ['Name', `${form.director2FirstName} ${form.director2LastName}`.trim()],
                ...(form.director2Address
                  ? [['Personal address', form.director2Address] as [string, string]]
                  : []),
                ...(form.director2Email
                  ? [['Email', form.director2Email] as [string, string]]
                  : []),
                ...(form.director2Phone
                  ? [['Contact number', form.director2Phone] as [string, string]]
                  : []),
              ]}
              onChange={() => goTo(3)}
            />
          )}
          <ReviewSection
            title="Current tenancy:"
            rows={[
              ['Business address', form.currentBusinessAddress],
              ['Landlord / agent', form.currentLandlord],
              ...(form.landlordEmail ? [['Email', form.landlordEmail] as [string, string]] : []),
              ...(form.landlordPhone ? [['Contact', form.landlordPhone] as [string, string]] : []),
              ['Can we contact them?', form.landlordContactOk === 'yes' ? 'Yes' : 'No'],
              ...(form.landlordContactOk === 'no' && form.landlordContactReason
                ? [['Why not', form.landlordContactReason] as [string, string]]
                : []),
            ]}
            onChange={() => goTo(4)}
          />
          <ReviewSection
            title="Photo ID:"
            rows={
              files.length
                ? files.map((f) => ['', f.name] as [string, string])
                : [['', 'None uploaded']]
            }
            onChange={() => goTo(5)}
          />
          <ReviewSection
            title="Proposed lease terms:"
            rows={[
              ['Commencing rent', form.commencingRent],
              ['Commencement date', form.commencementDate],
              ['Initial term', form.initialTerm],
              ['Options', form.leaseOptions],
              ['Annual rent increases', form.rentIncreases],
              ['Bond', form.bond],
              ['Tenancy use', form.tenancyUse],
              ...(form.specialConditions
                ? [['Special conditions', form.specialConditions] as [string, string]]
                : []),
            ]}
            onChange={() => goTo(6)}
          />
          <ReviewSection
            title="References:"
            rows={[
              ...(form.accountantName || form.accountantCompany
                ? [
                    [
                      'Accountant',
                      [form.accountantName, form.accountantCompany].filter(Boolean).join(', '),
                    ] as [string, string],
                  ]
                : []),
              ...(form.referenceName || form.referenceCompany
                ? [
                    [
                      'Reference',
                      [form.referenceName, form.referenceCompany, form.referenceRelationship]
                        .filter(Boolean)
                        .join(', '),
                    ] as [string, string],
                  ]
                : []),
              ...(form.otherInfo ? [['Other', form.otherInfo] as [string, string]] : []),
            ]}
            onChange={() => goTo(7)}
          />
          <ReviewSection
            title="Signed:"
            rows={[['', signature ? 'Yes' : 'No']]}
            onChange={() => goTo(8)}
          />
          {error && !error.field && <StepError>{error.message}</StepError>}
          <StepActions>
            <Button onClick={submit} loading={submitting}>
              Finalise &amp; Send
            </Button>
            <Button variant="ghost" onClick={back} disabled={submitting}>
              Back
            </Button>
          </StepActions>
        </div>
      )}
    </div>
  )
}
